import React from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import styles from "./AgentAuditSummary.module.scss";
import { DataService } from "../../store/DataService";

const AgentAuditSummary = (props) => {
    const navigate = useNavigate();
    const { date: paramDate } = useParams();
    const [searchParams, setSearchParams] = useSearchParams();
    
    // Initialize sort from URL params or defaults
    const initialSortKey = searchParams.get("sort") || 'lname';
    const initialSortDir = searchParams.get("dir") || 'asc';

    const [auditDate, setAuditDate] = React.useState(paramDate || new Date().toISOString().split('T')[0]);
    const [summaries, setSummaries] = React.useState([]);
    const [loading, setLoading] = React.useState(false);
    const [sortConfig, setSortConfig] = React.useState({ key: initialSortKey, direction: initialSortDir });

    const fetchSummaries = React.useCallback(async (date) => {
        setLoading(true);
        const ds = new DataService({ guid: props.globals.sessionguid });
        try {
            const data = await ds.getagentauditsummary(date);
            setLoading(false);
            if (data.ERROR) {
                alert(data.ERROR);
            } else {
                setSummaries(data.SUMMARIES || []);
            }
        } catch (err) {
            console.error("Failed to fetch audit summaries:", err);
            setLoading(false);
        }
    }, [props.globals.sessionguid]);

    React.useEffect(() => {
        if (props.globals.sessionguid) {
            fetchSummaries(auditDate);
        }
    }, [auditDate, props.globals.sessionguid, fetchSummaries]);

    const handleDateChange = (e) => {
        setAuditDate(e.target.value);
    };

    const handleAgentClick = (uaid) => {
        // Carry over the sort settings in the URL when going to detail
        const sortQuery = `?sort=${sortConfig.key}&dir=${sortConfig.direction}`;
        navigate(`/agentauditdetail/${uaid}/${auditDate}${sortQuery}`);
    };

    const exportRawData = async () => {
        setLoading(true);
        const ds = new DataService({ guid: props.globals.sessionguid });
        try {
            const data = await ds.getagentauditrawdump(auditDate);
            setLoading(false);
            if (data.ERROR) {
                alert(data.ERROR);
                return;
            }
            
            const tickets = data.TICKETS || [];
            if (tickets.length === 0) {
                alert("No raw ticket data to export.");
                return;
            }

            // Define CSV headers
            const headers = [
                "Ticket ID",
                "Ticket Created Date/Time",
                "Ticket Type",
                "Priority",
                "Practice ID",
                "Practice Name",
                "Event Date/Time",
                "Event Status",
                "Event User ID",
                "Event User Name"
            ];

            // Map data to CSV rows
            const csvRows = [
                headers.join(','),
                ...tickets.map(t => {
                    const rowValues = [
                        t.ticketid || "",
                        t.created_date || "",
                        `"${(t.ticket_type || "").replace(/"/g, '""')}"`,
                        t.priority || "",
                        t.practice_id || "",
                        `"${(t.practice_name || "").replace(/"/g, '""')}"`,
                        t.event_date || "",
                        t.event_status || "",
                        t.user_id || "",
                        `"${(t.user_name || "").replace(/"/g, '""')}"`
                    ];
                    return rowValues.join(',');
                })
            ];

            // Create Blob and trigger download (scalable for large payloads)
            const csvString = csvRows.join("\n");
            const blob = new Blob([csvString], { type: 'text/csv;charset=utf-8;' });
            const url = URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.setAttribute("href", url);
            link.setAttribute("download", `ticket_audit_raw_dump_${auditDate}.csv`);
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);
        } catch (err) {
            console.error("Failed to export raw data:", err);
            setLoading(false);
            alert("Error exporting raw data: " + err.message);
        }
    };

    const exportSummaryCSV = () => {
        if (summaries.length === 0) {
            alert("No summary data to export.");
            return;
        }

        const toDecimalMinutes = (val) => {
            if (val === undefined || val === null || val === "") return 0;
            let sec = 0;
            if (typeof val === 'number') {
                sec = val;
            } else if (typeof val === 'string') {
                const parts = val.split(':').map(p => parseInt(p, 10) || 0);
                if (parts.length === 3) sec = parts[0] * 3600 + parts[1] * 60 + parts[2];
                else if (parts.length === 2) sec = parts[0] * 60 + parts[1];
                else sec = parseInt(val, 10) || 0;
            }
            return Math.round((sec / 60) * 100) / 100;
        };

        const headers = [
            "Agent Name",
            "Assigned",
            "Completed",
            "Uncompleted",
            "Timeouts",
            "Active",
            "AHT (mins)",
            "Logged In (mins)",
            "Reserved (mins)",
            "Inprogress (mins)",
            "Wrapup (mins)",
            "Idle (mins)",
            "Unavailable (mins)"
        ];

        const totalsRow = [
            "\"TOTALS:\"",
            totals.assigned,
            totals.completed,
            totals.uncompleted,
            totals.timeouts,
            totals.active,
            toDecimalMinutes(totalAHT),
            toDecimalMinutes(totals.login),
            toDecimalMinutes(totals.reserved),
            toDecimalMinutes(totals.inprogress),
            toDecimalMinutes(totals.wrapup),
            toDecimalMinutes(totals.idle),
            toDecimalMinutes(totals.unavailable)
        ];

        const rows = sortedSummaries.map(s => {
            const agentName = s.lname && s.fname ? `"${s.lname}, ${s.fname}"` : `"${(s.name || s.uaid || "").replace(/"/g, '""')}"`;
            return [
                agentName,
                s.total_tickets || 0,
                s.completed_tickets || 0,
                s.uncompleted_tickets || 0,
                s.timeouts || 0,
                s.active || 0,
                toDecimalMinutes(s.aht !== undefined ? s.aht : s.aht_str),
                toDecimalMinutes(s.seconds_login !== undefined ? s.seconds_login : s.total_login),
                toDecimalMinutes(s.seconds_reserved !== undefined ? s.seconds_reserved : s.total_reserved),
                toDecimalMinutes(s.seconds_inprogress !== undefined ? s.seconds_inprogress : s.total_inprogress),
                toDecimalMinutes(s.seconds_wrapup !== undefined ? s.seconds_wrapup : s.total_wrapup),
                toDecimalMinutes(s.seconds_idle !== undefined ? s.seconds_idle : s.total_idle),
                toDecimalMinutes(s.seconds_unavailable !== undefined ? s.seconds_unavailable : s.total_unavailable)
            ].join(',');
        });

        const csvRows = [
            headers.join(','),
            totalsRow.join(','),
            ...rows
        ];

        const csvString = csvRows.join("\n");
        const blob = new Blob([csvString], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.setAttribute("href", url);
        link.setAttribute("download", `agent_audit_summary_${auditDate}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    };

    const requestSort = (key) => {
        let direction = 'asc';
        if (sortConfig.key === key && sortConfig.direction === 'asc') {
            direction = 'desc';
        }
        const newSort = { key, direction };
        setSortConfig(newSort);
        
        // Update URL params so they are preserved
        setSearchParams({ sort: key, dir: direction });
    };

    const sortedSummaries = React.useMemo(() => {
        let sortableItems = [...summaries];
        if (sortConfig !== null) {
            sortableItems.sort((a, b) => {
                let aVal = a[sortConfig.key];
                let bVal = b[sortConfig.key];

                // Special case for Name sorting: Sort by Last Name, then First Name
                if (sortConfig.key === 'lname' || sortConfig.key === 'name') {
                    const aName = `${a.lname}, ${a.fname}`.toLowerCase();
                    const bName = `${b.lname}, ${b.fname}`.toLowerCase();
                    if (aName < bName) return sortConfig.direction === 'asc' ? -1 : 1;
                    if (aName > bName) return sortConfig.direction === 'asc' ? 1 : -1;
                    return 0;
                }

                // Standard numeric/string sorting
                if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
                if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
                return 0;
            });
        }
        return sortableItems;
    }, [summaries, sortConfig]);

    const formatSeconds = (seconds) => {
        const totalSec = Math.round(seconds || 0);
        const m = Math.floor(totalSec / 60);
        const s = totalSec % 60;
        const h = Math.floor(m / 60);
        const mm = m % 60;
        return `${h.toString().padStart(2, '0')}:${mm.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
    };

    const totals = summaries.reduce((acc, s) => {
        acc.assigned += (s.total_tickets || 0);
        acc.completed += (s.completed_tickets || 0);
        acc.uncompleted += (s.uncompleted_tickets || 0);
        acc.timeouts += (s.timeouts || 0);
        acc.abandoned += (s.requeued || 0);
        acc.active += (s.active || 0);
        acc.inprogress += (s.seconds_inprogress || 0);
        acc.idle += (s.seconds_idle || 0);
        acc.reserved += (s.seconds_reserved || 0);
        acc.wrapup += (s.seconds_wrapup || 0);
        acc.unavailable += (s.seconds_unavailable || 0);
        acc.login += (s.seconds_login || 0);
        return acc;
    }, { assigned: 0, completed: 0, uncompleted: 0, timeouts: 0, abandoned: 0, active: 0, inprogress: 0, idle: 0, reserved: 0, wrapup: 0, unavailable: 0, login: 0 });

    const totalAHT = totals.completed > 0 ? Math.floor((totals.inprogress + totals.wrapup) / totals.completed) : 0;

    return (
        <React.Fragment>
            <div className={styles.title}>Agent Audit Summary</div>
            <div className={styles.page}>
                <div className={styles.filterbar}>
                    <label className={styles.formlabel}>
                        Select Date:
                        <input 
                            className={styles.forminput}
                            type="date" 
                            value={auditDate} 
                            onChange={handleDateChange} 
                        />
                    </label>
                    <button onClick={() => fetchSummaries(auditDate)} style={{marginLeft: '20px', padding: '5px 15px'}}>Refresh</button>
                    <button 
                        onClick={exportSummaryCSV} 
                        disabled={summaries.length === 0}
                        style={{
                            marginLeft: '10px', 
                            padding: '5px 15px', 
                            backgroundColor: summaries.length === 0 ? '#ccc' : '#0066cc', 
                            color: 'white', 
                            border: 'none', 
                            borderRadius: '4px', 
                            cursor: summaries.length === 0 ? 'default' : 'pointer'
                        }}
                    >
                        Export Summary CSV
                    </button>
                    <button 
                        onClick={exportRawData} 
                        disabled={summaries.length === 0}
                        style={{
                            marginLeft: '10px', 
                            padding: '5px 15px', 
                            backgroundColor: summaries.length === 0 ? '#ccc' : '#28a745', 
                            color: 'white', 
                            border: 'none', 
                            borderRadius: '4px', 
                            cursor: summaries.length === 0 ? 'default' : 'pointer'
                        }}
                    >
                        Export Raw Data
                    </button>
                    <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', color: '#555' }}>
                        <span style={{ width: '14px', height: '14px', backgroundColor: '#fffde7', border: '1px solid #ffe082', borderRadius: '3px', display: 'inline-block' }}></span>
                        <span><b>Legend:</b> Light Yellow = Unavailable / Logged Out</span>
                    </div>
                </div>

                {loading && <div className={styles.loading}>Loading audit summaries...</div>}

                {!loading && summaries.length > 0 && (
                    <div className={styles.reportcontainer}>
                        <table className={styles.audittable}>
                            <thead>
                                <tr className={styles.totalRow}>
                                    <th style={{width: '200px', textAlign: 'right'}}>TOTALS:</th>
                                    <th className={styles.statValue}>{totals.assigned}</th>
                                    <th className={styles.statValue}>{totals.completed}</th>
                                    <th className={styles.statValue}>{totals.uncompleted}</th>
                                    <th className={styles.statValue}>{totals.timeouts}</th>
                                    <th className={styles.statValue}>{totals.active}</th>
                                    <th className={styles.statValue}>{formatSeconds(totalAHT)}</th>
                                    <th className={styles.statValue}>{formatSeconds(totals.login)}</th>
                                    <th className={styles.statValue}>{formatSeconds(totals.reserved)}</th>
                                    <th className={styles.statValue}>{formatSeconds(totals.inprogress)}</th>
                                    <th className={styles.statValue}>{formatSeconds(totals.wrapup)}</th>
                                    <th className={styles.statValue}>{formatSeconds(totals.idle)}</th>
                                    <th className={styles.statValue}>{formatSeconds(totals.unavailable)}</th>
                                </tr>
                                <tr>
                                    <th 
                                        style={{width: '200px', cursor: 'pointer'}} 
                                        onClick={() => requestSort('lname')}
                                    >
                                        Agent Name {sortConfig.key === 'lname' ? (sortConfig.direction === 'asc' ? '↑' : '↓') : ''}
                                    </th>
                                    <th 
                                        className={styles.statValue} 
                                        style={{cursor: 'pointer'}} 
                                        onClick={() => requestSort('total_tickets')}
                                    >
                                        Assigned {sortConfig.key === 'total_tickets' ? (sortConfig.direction === 'asc' ? '↑' : '↓') : ''}
                                    </th>
                                    <th 
                                        className={styles.statValue} 
                                        style={{cursor: 'pointer'}} 
                                        onClick={() => requestSort('completed_tickets')}
                                    >
                                        Completed {sortConfig.key === 'completed_tickets' ? (sortConfig.direction === 'asc' ? '↑' : '↓') : ''}
                                    </th>
                                    <th 
                                        className={styles.statValue} 
                                        style={{cursor: 'pointer'}} 
                                        onClick={() => requestSort('uncompleted_tickets')}
                                    >
                                        Uncompleted {sortConfig.key === 'uncompleted_tickets' ? (sortConfig.direction === 'asc' ? '↑' : '↓') : ''}
                                    </th>
                                    <th 
                                        className={styles.statValue} 
                                        style={{cursor: 'pointer'}} 
                                        onClick={() => requestSort('timeouts')}
                                    >
                                        Timeouts {sortConfig.key === 'timeouts' ? (sortConfig.direction === 'asc' ? '↑' : '↓') : ''}
                                    </th>
                                    <th 
                                        className={styles.statValue} 
                                        style={{cursor: 'pointer'}} 
                                        onClick={() => requestSort('active')}
                                    >
                                        Active {sortConfig.key === 'active' ? (sortConfig.direction === 'asc' ? '↑' : '↓') : ''}
                                    </th>
                                    <th 
                                        className={styles.statValue} 
                                        style={{cursor: 'pointer'}} 
                                        onClick={() => requestSort('aht')}
                                    >
                                        AHT {sortConfig.key === 'aht' ? (sortConfig.direction === 'asc' ? '↑' : '↓') : ''}
                                    </th>
                                    <th 
                                        className={styles.statValue} 
                                        style={{cursor: 'pointer'}} 
                                        onClick={() => requestSort('seconds_login')}
                                    >
                                        Logged In {sortConfig.key === 'seconds_login' ? (sortConfig.direction === 'asc' ? '↑' : '↓') : ''}
                                    </th>
                                    <th 
                                        className={styles.statValue} 
                                        style={{cursor: 'pointer'}} 
                                        onClick={() => requestSort('seconds_reserved')}
                                    >
                                        Reserved {sortConfig.key === 'seconds_reserved' ? (sortConfig.direction === 'asc' ? '↑' : '↓') : ''}
                                    </th>
                                    <th 
                                        className={styles.statValue} 
                                        style={{cursor: 'pointer'}} 
                                        onClick={() => requestSort('seconds_inprogress')}
                                    >
                                        Inprogress {sortConfig.key === 'seconds_inprogress' ? (sortConfig.direction === 'asc' ? '↑' : '↓') : ''}
                                    </th>
                                    <th 
                                        className={styles.statValue} 
                                        style={{cursor: 'pointer'}} 
                                        onClick={() => requestSort('seconds_wrapup')}
                                    >
                                        Wrapup {sortConfig.key === 'seconds_wrapup' ? (sortConfig.direction === 'asc' ? '↑' : '↓') : ''}
                                    </th>
                                    <th 
                                        className={styles.statValue} 
                                        style={{cursor: 'pointer'}} 
                                        onClick={() => requestSort('seconds_idle')}
                                    >
                                        Idle {sortConfig.key === 'seconds_idle' ? (sortConfig.direction === 'asc' ? '↑' : '↓') : ''}
                                    </th>
                                    <th 
                                        className={styles.statValue} 
                                        style={{cursor: 'pointer'}} 
                                        onClick={() => requestSort('seconds_unavailable')}
                                    >
                                        Unavailable {sortConfig.key === 'seconds_unavailable' ? (sortConfig.direction === 'asc' ? '↑' : '↓') : ''}
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {sortedSummaries.map((s, index) => (
                                    <tr key={index} className={`${styles.trow} ${s.is_unavailable ? styles.unavailableRow : ''}`} onClick={() => handleAgentClick(s.uaid)}>
                                        <td className={styles.agentName}>{s.name}</td>
                                        <td className={styles.statValue}>{s.total_tickets}</td>
                                        <td className={styles.statValue} style={{color: '#28a745'}}>{s.completed_tickets}</td>
                                        <td className={styles.statValue} style={{color: s.uncompleted_tickets > 0 ? '#e67e22' : 'inherit'}}>{s.uncompleted_tickets || 0}</td>
                                        <td className={styles.statValue} style={{color: s.timeouts > 0 ? 'red' : 'inherit'}}>{s.timeouts}</td>
                                        <td className={styles.statValue} style={{color: s.active > 0 ? '#0066cc' : 'inherit'}}>{s.active || 0}</td>
                                        <td className={styles.statValue}>{s.aht_str || "00:00"}</td>
                                        <td className={styles.statValue}>{s.total_login || "00:00:00"}</td>
                                        <td className={styles.statValue}>{s.total_reserved || "00:00"}</td>
                                        <td className={styles.statValue}>{s.total_inprogress || "00:00"}</td>
                                        <td className={styles.statValue}>{s.total_wrapup || "00:00"}</td>
                                        <td className={styles.statValue}>{s.total_idle || "00:00"}</td>
                                        <td className={styles.statValue}>{s.total_unavailable || "00:00"}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}

                {!loading && summaries.length === 0 && (
                    <div className={styles.noresults}>No agent activity found for this date.</div>
                )}
            </div>
        </React.Fragment>
    );
};

export default AgentAuditSummary;
